import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LockKeyhole, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

import { own } from "@/lib/memberDerive";
import { normalizePassRequests } from "@/lib/coreShapes";
import { writeOutcome } from "@/lib/community";
import { normalizePassGrants, grantContext, dayOnly, dayTime, GRANT_STATE_LABEL } from "@/lib/v3Pass";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { mutate } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, PrivacyNote, ErrorNote, ConfirmDialog } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { ResultTable, ResultRow, AreaCard, ResponsibleCard } from "../components/v3app";
import { ShareAside, PrivateBanner, initialOf } from "../components/v3pass";

// Core 1.1: GET /member/access-pass/requests → requests[] (team requests) and grants[] (active and ended releases).
const useRequests = () => useCore("/member/access-pass/requests", (d) => ({ requests: normalizePassRequests(d), grants: normalizePassGrants(d) }));
const grantHref = (id) => `/access-pass/freigaben/${encodeURIComponent(id)}`;
const UNCLEAR = "Die Antwort des Servers war nicht eindeutig. Der aktuelle Stand wurde neu geladen – bitte prüfe ihn.";

function LoadState({ res, children }) {
  if (res.error === "notfound") return <Unavailable testid="grants-unavailable">Freigaben sind für dein Konto nicht abrufbar.</Unavailable>;
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (!res.data) return <Loading label="Freigaben werden geladen …" />;
  if (res.data.requests === null && res.data.grants === null) return <Unavailable testid="grants-format">Die Antwort des Servers hat ein unerwartetes Format. Es wird nichts angezeigt und nichts entschieden.</Unavailable>;
  return children(res.data);
}

// M110: own releases (active, requested, ended) – ended ones show metadata only.
export default function AccessPassGrants() {
  const res = useRequests();
  const [showEnded, setShowEnded] = useState(false);
  return (
    <div data-testid="access-pass-grants" data-m="M110">
      <V3Title title="Eigene Freigaben im Überblick" intro="Verwalte Empfänger, Zweck und Dauer jeder einzelnen Freigabe." testid="grants-title" />
      <LoadState res={res}>{({ requests, grants }) => {
        const open = (requests || []).filter((r) => r.state === "Angefragt");
        const active = (grants || []).filter((g) => !g.ended);
        const ended = (grants || []).filter((g) => g.ended);
        return (<>
          <ResultTable label="Deine Freigaben" testid="grants-table">
            {active.map((g) => <ResultRow key={g.id} initial={initialOf(g.recipient)} title={g.recipient} context={g.state === "unknown" ? `${grantContext(g)} · Status unbekannt` : grantContext(g)} to={grantHref(g.id)} testid={`grant-row-${g.id}`} />)}
            {open.map((r) => <ResultRow key={r.id} initial={initialOf(r.requester)} title={r.requester} context={`Anfrage · ${r.purpose} · ${r.fields.join(" · ") || "keine Felder angegeben"}`}
              to={`/access-pass/anfragen/${encodeURIComponent(r.id)}`} action="Prüfen" testid={`request-row-${r.id}`} />)}
            <ResultRow initial="B" title="Beendete Freigaben" context={ended.length ? `${ended.length} beendet · Nur erlaubte Metadaten sichtbar` : "Keine beendeten Freigaben"}
              onOpen={ended.length ? () => setShowEnded(!showEnded) : undefined} expanded={showEnded} action={ended.length ? "Öffnen" : ""} testid="grants-ended">
              {showEnded && <ul className="mt-[8px] space-y-[8px]" data-testid="grants-ended-list">{ended.map((g) => (
                <li key={g.id}><Link to={grantHref(g.id)} data-testid={`grant-ended-${g.id}`} className="block rounded-[10px] border border-v3-line bg-white px-[16px] py-[12px] text-[14px] transition-colors hover:border-v3-magenta">
                  <span className="font-bold text-v3-ink">{g.recipient}</span> <span className="text-v3-muted">· {grantContext(g)}</span></Link></li>))}</ul>}
            </ResultRow>
            <ResultRow initial="N" title="Neue Freigabe" context="Person und Angaben bewusst auswählen" to="/access-pass/teilen" testid="grants-new" />
          </ResultTable>
          {!active.length && !open.length && <p className="mt-[14px] text-[14px] text-v3-muted" data-testid="grants-empty">Du hast keine aktiven Freigaben und keine offenen Anfragen. Ohne deine Zustimmung sieht niemand deine Angaben.</p>}
          <V3Actions><Link to="/access-pass/teilen" data-testid="grants-select" className={V3_PRIMARY}>Freigabe auswählen</Link></V3Actions>
        </>);
      }}</LoadState>
      <div className="mt-[22px]"><PrivacyNote>Kontaktannahme, Gruppenbeitritt und Teilnahme geben deinen Pass niemals automatisch frei.</PrivacyNote></div>
    </div>
  );
}

// Shared action with confirmation: success only after the Core's ok; 404/409 or unclear answers reload the current state.
function useDecision(res) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (path, body, what, done) => {
    setBusy(true); setError("");
    const r = await mutate("post", path, body, what);
    setBusy(false);
    const o = writeOutcome(r, own(r.data, "ok") === true ? true : null, UNCLEAR);
    if (o.state === "saved") { toast.success(done); res.reload(); return true; }
    setError(o.error);
    if (o.state === "unclear" || r.status === 404 || r.status === 409) res.reload();
    return false;
  };
  return { busy, error, run };
}

function EndedGrant({ g }) {
  const revoked = g.state === "revoked";
  return (
    <div data-testid="grant-ended" data-m="M92">
      <V3Title title="Freigabe wurde beendet" intro="Die beendete Freigabe zeigt keine früheren privaten Feldwerte mehr." testid="grant-title" />
      <V3Columns layout="detail" aside={<><AreaCard title="Dein Bereich" text={g.recipient} testid="grant-area" /><ResponsibleCard /></>} main={<>
        <StatusBanner tone="lilacLarge" icon={LockKeyhole} title="Freigabe beendet" text={g.recipient} testid="grant-ended-banner" />
        <DetailList>
          <DetailRow label="Empfängerin / Empfänger" value={g.recipient} testid="grant-recipient" />
          <DetailRow label="Freigabe" value={revoked ? "Beendet · widerrufen" : "Beendet · abgelaufen"} testid="grant-state" />
          <DetailRow label={revoked ? "Widerrufen am" : "Abgelaufen am"} value={g.endedAt ? dayTime(g.endedAt, false) : "Nicht angegeben"} testid="grant-ended-at" />
          <DetailRow label="Weitere Angaben" value="Nicht mehr verfügbar" testid="grant-values" />
        </DetailList>
        <V3Actions>
          <Link to="/access-pass/freigaben" data-testid="grant-to-overview" className={V3_PRIMARY}>Zu meinen Freigaben</Link>
          <Link to="/access-pass" data-testid="grant-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

function ActiveGrant({ g, res }) {
  const [ask, setAsk] = useState(false);
  const d = useDecision(res);
  const revoke = async () => { if (await d.run(`/member/access-pass/grants/${encodeURIComponent(g.id)}/revoke`, undefined, "Die Freigabe wurde nicht widerrufen", "Freigabe widerrufen.")) setAsk(false); };
  return (
    <div data-testid="grant-detail" data-m="M20">
      <V3Title title="Freigabe ansehen und widerrufen" intro="Du kannst diese Freigabe jederzeit beenden. Danach sind die Angaben auch bei direktem Aufruf gesperrt." testid="grant-title" />
      <V3Columns layout="detail" aside={<ShareAside />} main={<>
        <PrivateBanner title={g.kind === "member" ? "Gezielte persönliche Freigabe" : "Freigabe an das VISIBILIS-Team"} text="Empfänger, Zweck, ausgewählte Angaben und Dauer gehören zu dieser einen Freigabe." testid="grant-banner" />
        <DetailList>
          <DetailRow label="Empfängerin / Empfänger" value={g.recipient} testid="grant-recipient" />
          <DetailRow label="Zweck" value={g.purpose} testid="grant-purpose" />
          <DetailRow label="Freigegebene Angaben" value={g.fields.join(" · ") || "Keine Felder angegeben"} testid="grant-fields" />
          <DetailRow label="Gültig bis" value={g.expiresAt ? dayTime(g.expiresAt) : "Ohne Enddatum"} testid="grant-expires" />
          <DetailRow label="Status" value={GRANT_STATE_LABEL[g.state]} testid="grant-state" />
        </DetailList>
        {d.error && <div className="mt-[12px]"><ErrorNote testid="grant-error">{d.error}</ErrorNote></div>}
        <V3Actions>
          <button type="button" onClick={() => setAsk(true)} disabled={d.busy} data-testid="grant-revoke" className={V3_PRIMARY}>Freigabe widerrufen</button>
          <Link to="/access-pass/freigaben" data-testid="grant-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
      <ConfirmDialog open={ask} onOpenChange={setAsk} title="Freigabe widerrufen?" testid="grant-revoke-dialog" confirmLabel="Jetzt widerrufen" busy={d.busy} onConfirm={revoke}
        description={`${g.recipient} sieht danach keine Werte dieser Freigabe mehr. Das lässt sich nicht rückgängig machen; eine neue Freigabe ist jederzeit möglich.`} />
    </div>
  );
}

function GrantView({ id }) {
  const res = useRequests();
  return (
    <LoadState res={res}>{({ grants }) => {
      const g = (grants || []).find((x) => x.id === id);
      if (!g) return (<><V3Title title="Freigabe nicht gefunden" testid="grant-title" /><Unavailable testid="grant-missing">Diese Freigabe gibt es nicht oder sie gehört nicht zu deinem Konto.</Unavailable>
        <Link to="/access-pass/freigaben" className={`${V3_SECONDARY} mt-[18px]`}>Zu meinen Freigaben</Link></>);
      return g.ended ? <EndedGrant g={g} /> : <ActiveGrant key={g.id} g={g} res={res} />;
    }}</LoadState>
  );
}

const durationText = (r) => (r.days ? `${r.days} Tage ab Freigabe` : r.expiresAt ? `bis ${dayOnly(r.expiresAt)}` : "Nicht angegeben");

function RequestView({ id }) {
  const res = useRequests();
  const [ask, setAsk] = useState(null);
  const d = useDecision(res);
  return (
    <LoadState res={res}>{({ requests }) => {
      const r = (requests || []).find((x) => x.id === id);
      if (!r) return (<><V3Title title="Anfrage nicht gefunden" testid="request-title" /><Unavailable testid="request-missing">Diese Anfrage gibt es nicht oder sie gehört nicht zu deinem Konto.</Unavailable></>);
      const decide = async () => { if (await d.run(`/member/access-pass/requests/${encodeURIComponent(r.id)}/decide`, { approve: ask === "approve" }, "Die Entscheidung wurde nicht gespeichert", ask === "approve" ? "Freigabe erteilt." : "Anfrage abgelehnt.")) setAsk(null); };
      return (
        <div data-testid="request-detail">
          <V3Title title="Anfrage zu deinem Access Pass" intro="Du entscheidest, ob die anfragende Stelle genau diese Angaben für diesen Zweck sehen darf." testid="request-title" />
          <V3Columns layout="detail" aside={<ShareAside />} main={<>
            <StatusBanner icon={ShieldQuestion} title="Nur mit deiner Zustimmung" text="Ohne Freigabe sieht niemand deine Angaben. Eine Ablehnung hat keine weiteren Folgen." testid="request-banner" />
            <DetailList>
              <DetailRow label="Anfrage von" value={r.requester} testid="request-requester" />
              <DetailRow label="Zweck" value={r.purpose} testid="request-purpose" />
              <DetailRow label="Angefragte Angaben" value={r.fields.join(" · ") || "Keine Felder angegeben"} testid="request-fields" />
              <DetailRow label="Dauer" value={durationText(r)} testid="request-duration" />
              <DetailRow label="Status" value={r.state} testid="request-state" />
            </DetailList>
            {d.error && <div className="mt-[12px]"><ErrorNote testid="request-error">{d.error}</ErrorNote></div>}
            {r.state === "Angefragt" ? (
              <V3Actions>
                <button type="button" onClick={() => setAsk("approve")} disabled={d.busy} data-testid="request-approve" className={V3_PRIMARY}>Diese Angaben freigeben</button>
                <button type="button" onClick={() => setAsk("decline")} disabled={d.busy} data-testid="request-decline" className={V3_SECONDARY}>Ablehnen</button>
              </V3Actions>
            ) : <p className="mt-[16px] text-[14px] text-v3-muted" data-testid="request-decided">Diese Anfrage ist bereits entschieden. <Link to="/access-pass/freigaben" className="font-bold text-v3-magenta hover:underline">Zu meinen Freigaben</Link></p>}
          </>} />
          <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} testid="request-dialog" busy={d.busy} onConfirm={decide}
            title={ask === "approve" ? "Angaben freigeben?" : "Anfrage ablehnen?"} confirmLabel={ask === "approve" ? "Freigeben" : "Ablehnen"}
            description={ask === "approve" ? `${r.requester} sieht für „${r.purpose}“ genau: ${r.fields.join(", ") || "keine Felder"} (${durationText(r)}). Du kannst die Freigabe jederzeit widerrufen.` : `${r.requester} erhält keine Angaben.`} />
        </div>
      );
    }}</LoadState>
  );
}

// Keyed by account and id, so nothing from a previous account or release stays visible.
export function GrantDetail() {
  const { id } = useParams();
  const { member } = useMemberAuth();
  return <GrantView key={`${member?.id || "anon"}:${id}`} id={id} />;
}

export function RequestDetail() {
  const { id } = useParams();
  const { member } = useMemberAuth();
  return <RequestView key={`${member?.id || "anon"}:${id}`} id={id} />;
}
